import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Building2, UserCog, BarChart3, LogOut, ChevronDown } from 'lucide-react';
import SaasAdminOverview from './SaasAdminOverview';
import SaasAdminOrganizations from './SaasAdminOrganizations';
import SaasAdminAdmins from './SaasAdminAdmins';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaasAdminDashboard({ setAdminAuth }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchAdmin();
  }, []);

  const fetchAdmin = async () => {
    try {
      const token = localStorage.getItem('saas_admin_token');
      const response = await axios.get(`${API}/saas-admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch {
      toast.error('Session expired');
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('saas_admin_token');
    localStorage.removeItem('saas_admin');
    setAdminAuth(false);
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400 text-lg">Loading...</p>
      </div>
    );
  }

  const isActive = (path) => {
    if (path === '/admin/dashboard' && location.pathname === '/admin/dashboard') return true;
    if (path !== '/admin/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Overview', icon: BarChart3 },
    { path: '/admin/dashboard/organizations', label: 'Organizations', icon: Building2 },
    { path: '/admin/dashboard/admins', label: 'Admin Users', icon: UserCog },
  ];

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col h-screen sticky top-0">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <div className="bg-amber-500 p-2 rounded-lg">
              <Settings className="w-5 h-5 text-black" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-manrope font-bold text-lg text-white">Keeboxs</h1>
              <p className="text-xs text-gray-500">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium flex items-center gap-3 transition-all text-sm ${
                isActive(item.path)
                  ? 'bg-amber-500/10 text-amber-500'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* Admin user section */}
        <div className="border-t border-gray-800">
          {/* Version info */}
          <div className="px-4 py-2.5 border-b border-gray-800">
            <p className="text-[10px] text-gray-600 text-center">v1.2.0 &middot; 28-Jun-2026</p>
            <p className="text-[10px] text-gray-600 text-center">&copy; 2026 Sysqua Solutions Pvt Ltd</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-full p-4 hover:bg-gray-800 transition-colors flex items-center gap-3"
              data-testid="admin-user-menu"
            >
              <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-500 font-semibold text-sm">
                {admin?.full_name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-white truncate">{admin?.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{admin?.email}</p>
              </div>
              <ChevronDown size={14} className={`text-gray-500 transition-transform ${showMenu ? 'rotate-180' : ''}`} />
            </button>

            {showMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-2 mx-2 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden shadow-xl">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 hover:bg-gray-700 transition-colors text-sm font-medium text-red-400 flex items-center gap-2"
                  data-testid="admin-logout-btn"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<SaasAdminOverview admin={admin} />} />
          <Route path="/organizations" element={<SaasAdminOrganizations />} />
          <Route path="/admins" element={<SaasAdminAdmins currentAdmin={admin} />} />
        </Routes>
      </main>
    </div>
  );
}
